/**
 * contractEngineConfig.ts — config silnika formatek dla klasy Umowy (contract),
 * R2 (docs/refactor/SILNIK-FORMATEK-ROLLOUT.md).
 *
 * Mirror `invoiceEngineConfig.ts`: KAŻDE pole core Umowy dostaje custom
 * renderer z rejestru `CONTRACT_CORE_RENDERERS` (contractCoreRenderers.tsx) —
 * full override, nie native delegation jak pilot commercial/internal (patrz
 * uzasadnienie w nagłówku tamtego rejestru). `labelSource: 'definition'` i
 * `coerceEmptyToNull: false` — jak w konfigu faktur, obie wartości kosmetyczne
 * (gałąź natywna silnika jest martwa, gdy KAŻDE pole core ma renderer).
 *
 * Rejestr niesie 13 pól wspólnych dokumentu ORAZ 13 pól umowy
 * (`document_contract_details` — numer, status umowy, przedmiot, okres, wartość,
 * z regułami poufności; ADR-0109b). Zakładka „Dane" rysuje się z układu jak u faktur:
 * pole pokazuje się tylko wtedy, gdy stoi w układzie formularza.
 */

import type {
  CoreFieldConfig,
  KartotekaEngineConfig,
} from '@/components/kartoteki/engine/engine-config';

import { CONTRACT_CORE_RENDERERS, type ContractFormData } from './contractCoreRenderers';
import { documentCompanyFieldConfig } from './documentCompanyRenderer';

/**
 * Ground-truth `fieldType` per slug (z seeda, `apps/api/lib/seed-system-modules.ts`
 * → `contracts.coreFields`) — używany WYŁĄCZNIE przez fallback statyczny silnika
 * (403 na metadanych modułu). Renderer i tak owns widget, więc wartość jest
 * kosmetyczna; `source` zostaje 'select' zgodnie z seedem mimo że renderer
 * (wolny tekst) odbiega od tego — rozjazd istniał już przed silnikiem.
 */
const CONTRACT_FIELD_TYPES: Record<string, string> = {
  system_number: 'varchar',
  title: 'varchar',
  status: 'select',
  document_date: 'date',
  description: 'text',
  urgency: 'select',
  owner_user_id: 'reference',
  category_id: 'select',
  notes: 'text',
  rejection_reason: 'text',
  source: 'select',
  external_id: 'varchar',
  external_source: 'varchar',
  // Pola umowy (`document_contract_details`, ADR-0109b) — typy wg kolumn tabeli.
  contract_number: 'varchar',
  contract_number_missing: 'boolean',
  contract_status: 'select',
  subject_matter: 'text',
  subject_matter_confidential: 'boolean',
  period_from: 'date',
  period_to: 'date',
  period_indefinite: 'boolean',
  period_duration_value: 'integer',
  period_duration_unit: 'select',
  contract_value: 'numeric',
  value_confidential: 'boolean',
  contract_value_description: 'text',
};

// `CONTRACT_CORE_FIELD_SLUGS` DERYWOWANE z kluczy rejestru rendererów — slug nie zniknie po cichu
// (usunięcie renderera złamałoby build/render). Dlatego umowa NIE ma dedykowanego test-strażnika
// obecności kategorii na formularzu (jak pismo/handlowe/wewnętrzne z ręczną listą slugów) —
// kompletność osi jest tu gwarantowana mechaniką derywacji.
const CONTRACT_CORE_FIELD_SLUGS = Object.keys(CONTRACT_CORE_RENDERERS);

const fields: Record<string, CoreFieldConfig<ContractFormData>> = {};
for (const slug of CONTRACT_CORE_FIELD_SLUGS) {
  fields[slug] = {
    fieldType: CONTRACT_FIELD_TYPES[slug] ?? 'varchar',
    renderer: CONTRACT_CORE_RENDERERS[slug]!,
  };
}
// Firma dokumentu — tylko do odczytu, opt-in z układu (NIE w coreFieldSlugs): patrz documentCompanyRenderer.tsx.
fields.company_id = documentCompanyFieldConfig<ContractFormData>();

export const contractEngineConfig: KartotekaEngineConfig<ContractFormData> = {
  moduleSlug: 'contracts',
  testIdPrefix: 'contract',
  coreFieldSlugs: CONTRACT_CORE_FIELD_SLUGS,
  fields,
  labelSource: 'definition',
  coerceEmptyToNull: false,
};
